// HEADER-DOCS-012: AddLinkModal
// Spec: §4.3, §16.3 (duplicate link dedup -> 409 link_already_added)

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  documentsApi,
  DocumentDto,
  DocType,
  DOC_TYPES,
  DOC_TYPE_LABELS,
  httpStatusOf,
  isApiError,
} from '../../../../services/camMirrorService';
import SelectInput from 'components/input/SelectInput';
import TextInput from 'components/input/textInput';

interface AddLinkModalProps {
  studentId: number;
  defaultDocType?: DocType;
  onClose: () => void;
  onAdded: (doc: DocumentDto) => void;
}

export const AddLinkModal: React.FC<AddLinkModalProps> = ({
  studentId,
  defaultDocType = 'essay_draft',
  onClose,
  onAdded,
}) => {
  const [url, setUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [docType, setDocType] = useState<DocType>(defaultDocType);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const trimmedUrl = url.trim();

  // Preview only; the backend computes the stored linkSourceLabel itself.
  const sourceLabel = useMemo(() => deriveLinkSource(trimmedUrl), [trimmedUrl]);

  const urlValid = useMemo(() => {
    try {
      return new URL(trimmedUrl).protocol === 'https:';
    } catch {
      return false;
    }
  }, [trimmedUrl]);

  const onSubmit = async () => {
    if (!urlValid || !displayName.trim() || submitting) return;
    setError(null);
    setIsDuplicate(false);
    setSubmitting(true);
    try {
      const doc = await documentsApi.create({
        studentId,
        linkUrl: trimmedUrl,
        displayName: displayName.trim(),
        docType,
      });
      toast.success('Link added.');
      onAdded(doc);
    } catch (err) {
      const code = isApiError(err) ? err.response.data.code : undefined;
      if (code === 'link_already_added') {
        setIsDuplicate(true);
        setError('This link is already saved for this student. Scroll the list to find it.');
      } else if (code === 'financial_doc_blocked') {
        setError(
          'That looks like a financial document. Share it through the school financial aid portal instead.'
        );
      } else if (httpStatusOf(err) === 429) {
        setError("You've added a lot recently. Please wait a few minutes and try again.");
      } else {
        setError("We couldn't save that link. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  let urlHelper: string | null = null;
  if (!trimmedUrl) urlHelper = 'Must start with https://';
  else if (!urlValid) urlHelper = 'URL must use HTTPS';
  else if (sourceLabel) urlHelper = `Detected: ${sourceLabel}`;

  return (
    <Dialog
      open
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      sx={{
        // Joy listbox portals to body at z-index 1000, under this dialog.
        '& ~ [role="listbox"]': {
          '--unstable_popup-zIndex': '1400',
          zIndex: '1400 !important',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Add link
        <IconButton onClick={onClose} size="small" aria-label="Close" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5, '& > *': { width: '100%' } }}>
          <TextInput
            label="URL"
            name="url"
            values={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://docs.google.com/document/d/..."
            feedback={urlHelper}
            isMissing={trimmedUrl.length > 0 && !urlValid}
            otherProps={{
              autoFocus: true,
              slotProps: { input: { maxLength: 2048 } },
            }}
          />
          <TextInput
            label="Display name"
            name="displayName"
            values={displayName}
            onChange={(e) => setDisplayName(e.target.value.slice(0, 200))}
            placeholder="e.g. UC essay draft 2"
          />
          <SelectInput
            label="Document type"
            values={docType}
            option={DOC_TYPES.map((t) => ({ value: t, name: DOC_TYPE_LABELS[t] }))}
            onChange={(e) => setDocType(e.target.value as DocType)}
          />
          {urlValid && sourceLabel && (
            <Chip label={sourceLabel} size="small" sx={{ alignSelf: 'flex-start' }} />
          )}
          <Typography variant="caption" color="text.secondary">
            GradMap doesn't check link permissions. If the link stops working, ask the person who
            shared it to update sharing.
          </Typography>
          {error && <Alert severity={isDuplicate ? 'warning' : 'error'}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitting || !urlValid || !displayName.trim()}
        >
          {submitting ? <CircularProgress size={16} /> : 'Add link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Friendly label for well-known link hosts (matched on hostname, not substring of the whole URL).
function deriveLinkSource(raw: string): string | null {
  if (!raw) return null;
  let host: string;
  let path: string;
  try {
    const u = new URL(raw);
    host = u.hostname.toLowerCase();
    path = u.pathname.toLowerCase();
  } catch {
    return null;
  }
  const is = (domain: string) => host === domain || host.endsWith(`.${domain}`);
  if (host === 'docs.google.com') {
    if (path.startsWith('/document/')) return 'Google Docs';
    if (path.startsWith('/spreadsheets/')) return 'Google Sheets';
    if (path.startsWith('/presentation/')) return 'Google Slides';
    return 'Google Drive';
  }
  if (host === 'drive.google.com') return 'Google Drive';
  if (is('dropbox.com') || is('paper.dropbox.com')) return 'Dropbox';
  if (is('notion.so') || is('notion.site')) return 'Notion';
  if (is('onedrive.live.com') || is('sharepoint.com') || host === '1drv.ms') return 'OneDrive';
  return 'Other';
}
